import CarsTableBidsBuyer from '@components/CarsTableBidsBuyer';
import CarsTableBidsCell from '@components/CarsTableBidsCell';
import CarsTableBidSuccessStatus from '@components/CarsTableBidSuccessStatus';
import CarsTableBoughtKVPS from '@components/CarsTableBoughtKVPS';
import CarsTableDeliveryAddress from '@components/CarsTableDeliveryAddress';
import UnitValue from '@components/UnitValue';
import { Chip } from '@material-ui/core';
import { InfoOutlined } from '@material-ui/icons';
import WarningIcon from '@material-ui/icons/Warning';
import { DateTime } from 'luxon';
import { UserPermissions } from 'src/hooks/useUserPermissions';
import { BID_DATE_TIME_FORMAT, DATE_TIME_FORMAT_NO_TIME, formatDate } from 'src/modules/date-helpers';
import { TransportationState } from 'src/modules/generated/api';
import { tr } from 'src/modules/i18n-helpers';
import {
  bidAutomationTypes,
  getLabel,
  transportDeliveryStatus,
  transportOptionProvider,
  transportOptionStatus,
} from 'src/modules/labels';
import UserRoles from 'src/types/UserRoles';

import { NumericCell, TextCell, TextCellFallback } from '../cells';
import { ColumnSize } from '../constants';
import { BidsTableNextItem, BidTypes, StrictColumnDef } from '../types';

// Pending column needs a bigger size due to Delete button, if dealerId is shown more space is needed
const getBidUserDealerMinSize = (bidType?: BidTypes, showDealer?: boolean): number => {
  let baseSize: number;

  switch (bidType) {
    case BidTypes.Pending:
      baseSize = 260;
      break;
    case BidTypes.Past:
      baseSize = 230;
      break;
    case BidTypes.Orders:
      baseSize = 280;
      break;
    default:
      baseSize = 260;
  }
  const dealerColumnWidth = 80;
  return showDealer ? baseSize + dealerColumnWidth : baseSize;
};

export const bidColumns = (bidType?: BidTypes, showDealer?: boolean): StrictColumnDef<BidsTableNextItem>[] => [
  {
    id: 'bidUserDealer',
    cell: ({ row: { original } }) => <CarsTableBidsCell bids={original.bids} bidInfos={original} />,
    meta: {
      align: 'center',
      export: {
        enabled: true,
        exports: [
          { key: 'bids.bid', label: 'carsTableNext.filter.groups.bids' },
          { key: 'bids.status', label: 'carsTableNext.columns.bidStatus' },
          { key: 'bids.biddingTime', label: 'carsTableNext.columns.biddingTime' },
          { key: 'bids.user.name', label: 'navigation.dealer' },
          { key: 'bids.dealer.id', label: 'carsTableNext.filter.bidCreatorDealerId' },
        ],
      },
      fallback: () => <TextCellFallback textWidth="80%" style={{ textAlign: 'center' }} />,
    },
    size: ColumnSize.lg,
    minSize: getBidUserDealerMinSize(bidType, showDealer),
    enableSorting: false,
  },
  {
    id: 'successStatus',
    accessorFn: (bid) => bid,
    cell: ({ row: { original } }) => <CarsTableBidSuccessStatus bid={original} />,
    meta: {
      export: {
        enabled: true,
      },
      fallback: () => <TextCellFallback textWidth="80%" />,
    },
    enableSorting: false,
  },
  {
    id: 'buyer',
    accessorFn: (bid) => bid,
    cell: ({ row: { original } }) => <CarsTableBidsBuyer bids={original.bids} />,
    meta: {
      columnVisible: false,
      export: {
        enabled: true,
      },
      roles: [UserRoles.ADMIN],
      onlyForTable: 'orders',
      fallback: () => <TextCellFallback textWidth="80%" />,
    },
    enableSorting: false,
  },
  {
    id: 'buyingKVPS',
    accessorFn: (bid) => bid,
    cell: ({ row: { original } }) => <CarsTableBoughtKVPS bids={original.bids} />,
    meta: {
      columnVisible: false,
      export: {
        enabled: true,
      },
      roles: [UserRoles.ADMIN],
      onlyForTable: 'orders',
      fallback: () => <TextCellFallback textWidth="80%" />,
    },
    enableSorting: false,
  },
  {
    id: 'purchaseWithAutomation',
    accessorFn: (bid) => bid,
    cell: ({ row: { original } }) => (
      <TextCell
        style={{ textAlign: 'left' }}
        text={getLabel(
          bidAutomationTypes,
          original.bids?.find((b) => (b.status === 'BOUGHT' || b.status === 'HIGHEST') && b.automatedBy)?.automatedBy ||
            '',
        )}
      />
    ),
    meta: {
      columnVisible: false,
      export: {
        enabled: true,
      },
      onlyForTable: 'orders',
      permissions: [UserPermissions.PURCHASE_AGENT],
      fallback: () => <TextCellFallback textWidth="80%" />,
      roles: [UserRoles.ADMIN],
    },
    enableSorting: true,
  },
  {
    id: 'purchasePrice',
    accessorFn: (bid) => bid,
    cell: ({ row: { original } }) => (
      <NumericCell value={original.bids?.find((bid) => bid.allocatedBid?.amount)?.allocatedBid ?? '-'} />
    ),
    meta: {
      export: {
        enabled: true,
      },
      fallback: () => <TextCellFallback textWidth="80%" />,
      onlyForTable: 'orders',
    },
    enableSorting: false,
  },
  {
    id: 'deliveryAddress',
    accessorFn: (bid) => bid.deliveryAddress,
    cell: ({ row: { original } }) => <CarsTableDeliveryAddress address={original.deliveryAddress} />,
    meta: {
      columnVisible: false,
      export: {
        enabled: true,
        exports: [
          { key: 'deliveryAddress.companyName', label: 'bids.transport.deliveryAddress.companyName' },
          { key: 'deliveryAddress.street', label: 'bids.transport.deliveryAddress.street' },
          { key: 'deliveryAddress.zip', label: 'bids.transport.deliveryAddress.zip' },
          { key: 'deliveryAddress.city', label: 'bids.transport.deliveryAddress.city' },
          { key: 'deliveryAddress.contactPersonName', label: 'bids.transport.deliveryAddress.contactPersonName' },
          { key: 'deliveryAddress.email', label: 'bids.transport.deliveryAddress.email' },
          { key: 'deliveryAddress.phoneNumber', label: 'bids.transport.deliveryAddress.phoneNumber' },
          { key: 'deliveryAddress.betriebsId', label: 'bids.transport.deliveryAddress.betriebsId' },
          { key: 'deliveryAddress.addressCode', label: 'bids.transport.deliveryAddress.addressCode' },
        ],
      },
      onlyForTable: 'orders',
      fallback: () => <TextCellFallback textWidth="80%" />,
    },
    enableSorting: false,
    size: ColumnSize.md,
  },
  {
    id: 'deliveryStatus',
    accessorFn: (bid) => bid.transport?.deliveryStatus,
    cell: ({ row: { original } }) => (
      <TextCell
        style={{ textAlign: 'left' }}
        text={getLabel(transportDeliveryStatus, original.transport?.deliveryStatus || '')}
      />
    ),
    meta: {
      columnVisible: false,
      export: {
        enabled: false,
      },
      onlyForTable: 'orders',
      fallback: () => <TextCellFallback textWidth="80%" />,
      roles: [UserRoles.ADMIN],
    },
    enableSorting: false,
    size: ColumnSize.sm,
  },
  {
    id: 'transport',
    accessorFn: (bid) => bid.transport,
    cell: ({ row: { original } }) =>
      original.transport ? (
        <TextCell
          text={
            original.transport?.option?.provider
              ? getLabel(transportOptionProvider, original.transport.option.provider)
              : ''
          }
          supportText={
            original.transport?.option?.cost ? (
              <UnitValue
                value={original.transport?.option.cost.amount}
                unit={original.transport?.option.cost.currency}
              />
            ) : undefined
          }
        />
      ) : (
        <TextCell text={tr('common.unavailable')} />
      ),
    meta: {
      columnVisible: false,
      export: {
        enabled: true,
        exports: [
          { key: 'transport.option.provider', label: 'carsTableNext.columns.transportOption' },
          { key: 'transport.option.cost', label: 'carsTableNext.columns.transportationPrice' },
        ],
      },
      onlyForTable: 'orders',
      roles: [UserRoles.ADMIN],
      fallback: () => <TextCellFallback textWidth="80%" />,
    },
    enableSorting: false,
    size: 170,
  },
  {
    id: 'sourceAddress',
    accessorFn: (bid) => bid.transport?.sourceAddress,
    cell: ({ row: { original } }) => {
      const statusWaiting = original.transport?.status === TransportationState.WaitingForValidSourceAddress;
      const statusVwfs = original.transport?.status === TransportationState.WaitingForValidSourceAddressFromVwfs;
      if (!original.transport?.sourceAddress) {
        return <TextCell text={tr('common.unavailable')} />;
      }
      return (
        <>
          {!statusVwfs && <CarsTableDeliveryAddress address={original.transport?.sourceAddress} />}
          {statusWaiting && (
            <Chip
              style={{ width: 'fit-content' }}
              size="small"
              icon={<WarningIcon fontSize="small" />}
              label={
                <span style={{ whiteSpace: 'nowrap' }}>
                  {tr(getLabel(transportOptionStatus, original.transport?.status!))}
                </span>
              }
            />
          )}
          {statusVwfs && (
            <Chip
              style={{ width: 'fit-content', height: 'fit-content', whiteSpace: 'pre-line' }}
              size="small"
              variant="outlined"
              icon={<InfoOutlined fontSize="small" />}
              label={
                <span style={{ whiteSpace: 'pre-line' }}>
                  {tr(getLabel(transportOptionStatus, original.transport?.status!))}
                </span>
              }
            />
          )}
        </>
      );
    },
    meta: {
      columnVisible: false,
      export: {
        enabled: true,
        exports: [
          { key: 'transport.sourceAddress.companyName', label: 'bids.transport.sourceAddress.companyName' },
          { key: 'transport.sourceAddress.street', label: 'bids.transport.sourceAddress.street' },
          { key: 'transport.sourceAddress.zip', label: 'bids.transport.sourceAddress.zip' },
          { key: 'transport.sourceAddress.city', label: 'bids.transport.sourceAddress.city' },
          { key: 'transport.sourceAddress.contactPersonName', label: 'bids.transport.sourceAddress.contactPersonName' },
          { key: 'transport.sourceAddress.email', label: 'bids.transport.sourceAddress.email' },
          { key: 'transport.sourceAddress.phoneNumber', label: 'bids.transport.sourceAddress.phoneNumber' },
        ],
      },
      onlyForTable: 'orders',
      roles: [UserRoles.ADMIN],
      fallback: () => <TextCellFallback textWidth="80%" />,
    },
    size: ColumnSize.md,
    enableSorting: false,
  },
  {
    id: 'sourceAddressZip',
    accessorFn: (bid) => bid.transport?.sourceAddress?.zip,
    cell: ({ row: { original } }) => <TextCell text={original.transport?.sourceAddress?.zip ?? ''} />,
    meta: {
      columnVisible: false,
      export: {
        enabled: true,
        key: 'transport.sourceAddress.zip',
      },
      onlyForTable: 'orders',
      fallback: () => <TextCellFallback textWidth="80%" />,
    },
    enableSorting: false,
  },
  {
    id: 'created',
    accessorFn: (bid) => bid,
    cell: ({ row: { original } }) => {
      const date = original.created;
      const formattedDate = date ? DateTime.fromISO(date).toString() : undefined;
      return (
        <TextCell
          style={{ textAlign: 'center' }}
          text={formattedDate ? formatDate(formattedDate, BID_DATE_TIME_FORMAT) : ''}
        />
      );
    },
    meta: {
      columnVisible: false,
      export: {
        enabled: true,
      },
      fallback: () => <TextCellFallback textWidth="80%" />,
    },
    enableSorting: false,
  },
  {
    id: 'biddingTime',
    accessorFn: (bid) => bid,
    cell: ({ row: { original } }) => {
      const date = original.biddingTime;
      const formattedDate = date ? DateTime.fromISO(date).toString() : undefined;
      return (
        <TextCell
          style={{ textAlign: 'center' }}
          text={formattedDate ? formatDate(formattedDate, BID_DATE_TIME_FORMAT) : ''}
        />
      );
    },
    meta: {
      columnVisible: false,
      export: {
        enabled: true,
      },
      fallback: () => <TextCellFallback textWidth="80%" />,
    },
  },
  {
    id: 'sentTime',
    accessorFn: (bid) => bid,
    cell: ({ row: { original } }) => {
      const date = original.sentTime;
      const formattedDate = date ? DateTime.fromISO(date).toString() : undefined;
      return (
        <TextCell
          style={{ textAlign: 'center' }}
          text={formattedDate ? formatDate(formattedDate, BID_DATE_TIME_FORMAT) : ''}
        />
      );
    },
    meta: {
      columnVisible: false,
      export: {
        enabled: true,
      },
      fallback: () => <TextCellFallback textWidth="80%" />,
    },
  },
  {
    id: 'boughtTime',
    accessorFn: (bid) => bid,
    cell: ({ row: { original } }) => {
      const date = original.dateOfBuy || original.boughtTime;
      const formattedDate = date ? DateTime.fromISO(date).toString() : undefined;
      return (
        <TextCell
          style={{ textAlign: 'center' }}
          text={formattedDate ? formatDate(formattedDate, DATE_TIME_FORMAT_NO_TIME) : ''}
        />
      );
    },
    meta: {
      columnVisible: false,
      export: {
        enabled: true,
      },
      onlyForTable: 'orders',
      fallback: () => <TextCellFallback textWidth="80%" />,
      sortKey: 'dateOfBuy',
    },
  },
];
